import React from 'react';

import type { FilterState } from '../../../components/PageFilters/types';
import { useDebouncedCallback } from '../../../utils/hooks';
import { dateCreatedFilterKey } from '../components/WorkspacePageFilters/DateCreatedFilter';
import { ownerFilterKey } from '../components/WorkspacePageFilters/OwnerFilter';
import { tagsFilterKey } from '../components/WorkspacePageFilters/TagsFilter';

type WorkspaceFiltersRecord = Record<string, FilterState<unknown>>;

export const useWorkspaceFiltering = () => {
  const [filters, setFilters] = React.useState<WorkspaceFiltersRecord>({});
  const [searchText, setSearchText] = React.useState<string>('');
  const [debouncedSearchText, setDebouncedSearchText] = React.useState<string>('');

  const debouncedHandleSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearchText(value);
  }, 500);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    debouncedHandleSearch(value);
  };

  // Construct AIP filter
  const aipFilter = React.useMemo(() => {
    const filterParts: string[] = [];

    if (debouncedSearchText?.trim()) filterParts.push(`"${debouncedSearchText.trim()}"`);

    const owner = filters[ownerFilterKey]?.value as string | undefined;
    if (owner) filterParts.push(`creator="${owner}"`);

    const dateCreated = filters[dateCreatedFilterKey]?.value as { dateStart?: string; dateEnd?: string } | undefined;
    if (dateCreated?.dateStart) filterParts.push(`create_time >= "${dateCreated.dateStart}"`);
    if (dateCreated?.dateEnd) filterParts.push(`create_time <= "${dateCreated.dateEnd}"`);

    const tags = filters[tagsFilterKey]?.value as string[] | undefined;
    if (tags?.length) {
      const tagFilters = tags.map((tag) => `tags:"${tag}"`).join(' OR ');
      filterParts.push(`(${tagFilters})`);
    }

    return filterParts.join(' AND ') || undefined;
  }, [filters, debouncedSearchText]);

  const resetFilters = () => {
    setFilters({});
    setSearchText('');
    setDebouncedSearchText('');
    debouncedHandleSearch.cancel();
  };

  return {
    filters,
    setFilters,
    searchText,
    handleSearchChange,
    aipFilter,
    resetFilters,
  };
};
